import { useForm, useWatch } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { DialogClose } from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { type FormProps } from "@/types/Form";

import { useGetCrewMembersQuery } from "@/app/api/slices/crewMembersApiSlice";
import { useGetFleetVesselsQuery } from "@/app/api/slices/fleet-manager/fleetApiSlice";
import { ButtonLoader } from "@/components/ButtonLoader";
import { DepartmentAndPositionsDependentSelects } from "@/components/forms/DepartmentAndPositionsDependentSelects";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import { type User } from "@/types/User";
import { type Vessel } from "@/types/Vessel";
import { zodResolver } from "@hookform/resolvers/zod";
import { formSchema, type FormType } from "./formSchema";

export const ImportCrewMemberForm = ({
	onSubmit,
	isLoading,
	defaultValues = {
		vessel: "",
		crewMember: "",
		department: "",
		position: "",
		leavePlan: "",
	},
}: FormProps<FormType>) => {
	const form = useForm<FormType>({
		resolver: zodResolver(formSchema),
		defaultValues: defaultValues,
	});
	const { handleSubmit, control, setValue } = form;

	const vesselValue = useWatch({
		control,
		name: "vessel",
	});

	const auth = useAuth();
	const activeVesselId = auth?.user?.activeVesselId;
	const {
		data: vesselsResponse,
		isLoading: isLoadingVessels,
		isFetching: isFetchingVessels,
	} = useGetFleetVesselsQuery({});
	const {
		data: crewMembersResponse,
		isLoading: isLoadingCrewMembers,
		isFetching: isFetchingCrewMembers,
	} = useGetCrewMembersQuery(
		{
			vesselId: vesselValue,
		},
		{ skip: !vesselValue },
	);

	return (
		<Form {...form}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="grid w-full items-center gap-4">
					<div className={`space-y-1.5} flex flex-col`}>
						<FormField
							control={control}
							name="vessel"
							render={({ field }) => (
								<FormItem>
									<div className="grid grid-cols-1 items-center gap-3 sm:grid-cols-2">
										<div className="flex items-center justify-start gap-2">
											<FormLabel className="whitespace-nowrap">Vessel</FormLabel>
											<FormMessage />
										</div>
										{!isLoadingVessels && !isFetchingVessels && (
											<FormControl>
												<Select onValueChange={field.onChange} defaultValue={field.value}>
													<SelectTrigger>
														<SelectValue placeholder="Select Vessel" />
													</SelectTrigger>
													<SelectContent>
														<SelectGroup>
															{vesselsResponse?.data
																.filter((vessel: Vessel) => vessel.id !== activeVesselId)
																.map((vessel: Vessel) => {
																	return (
																		<SelectItem key={vessel.name} value={String(vessel.id)}>
																			{vessel.name}
																		</SelectItem>
																	);
																})}
														</SelectGroup>
													</SelectContent>
												</Select>
											</FormControl>
										)}
										{(isLoadingVessels || isFetchingVessels) && (
											<Skeleton className="h-10 w-full rounded-md" />
										)}
									</div>
								</FormItem>
							)}
						/>
					</div>
					<div className={`space-y-1.5} flex flex-col`}>
						<FormField
							control={control}
							name="crewMember"
							render={({ field }) => (
								<FormItem>
									<div className="grid grid-cols-1 items-center gap-3 sm:grid-cols-2">
										<div className="flex items-center justify-start gap-2">
											<FormLabel className="whitespace-nowrap">Crew Member</FormLabel>
											<FormMessage />
										</div>
										{!isLoadingCrewMembers && !isFetchingCrewMembers && (
											<FormControl>
												<Select
													onValueChange={field.onChange}
													defaultValue={field.value}
													disabled={!vesselValue}
												>
													<SelectTrigger>
														<SelectValue placeholder="Select Crew Member" />
													</SelectTrigger>
													<SelectContent>
														<SelectGroup>
															{crewMembersResponse?.data.map((crewMember: User) => {
																return (
																	<SelectItem
																		key={`crew-member-${crewMember.id}`}
																		value={String(crewMember.id)}
																	>
																		{crewMember.firstName} {crewMember.lastName}
																	</SelectItem>
																);
															})}
														</SelectGroup>
													</SelectContent>
												</Select>
											</FormControl>
										)}
										{(isLoadingCrewMembers || isFetchingCrewMembers) && (
											<Skeleton className="h-10 w-full rounded-md" />
										)}
									</div>
								</FormItem>
							)}
						/>
					</div>
					<DepartmentAndPositionsDependentSelects
						control={control}
						setValue={setValue}
						vesselId={Number(activeVesselId)}
					/>
					<div className={`space-y-1.5} flex flex-col`}>
						<FormField
							control={form.control}
							name="leavePlan"
							render={({ field }) => (
								<FormItem className="space-y-3">
									<div className="flex items-center gap-2">
										<FormLabel>Leave Plan</FormLabel>
										<FormMessage />
									</div>
									<FormControl>
										<RadioGroup
											onValueChange={field.onChange}
											defaultValue={field.value}
											className="flex flex-col space-y-1"
										>
											<FormItem className="flex items-center space-x-3 space-y-0">
												<FormControl>
													<RadioGroupItem value="import_with_current_leave_plan" />
												</FormControl>
												<FormLabel className="font-normal">
													Import with current leave plan
												</FormLabel>
											</FormItem>
											<FormItem className="flex items-center space-x-3 space-y-0">
												<FormControl>
													<RadioGroupItem value="import_with_auto_generated_leave_plan" />
												</FormControl>
												<FormLabel className="font-normal">Use auto-generated leave plan</FormLabel>
											</FormItem>
											<div>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem value="import_without_leave_plan" />
													</FormControl>
													<FormLabel className="font-normal">Import without leave plan</FormLabel>
												</FormItem>
												<FormDescription>
													Rotation has to be created from leave plan calendar later.
												</FormDescription>
											</div>
										</RadioGroup>
									</FormControl>
								</FormItem>
							)}
						/>
					</div>
					<div className="flex justify-between">
						<DialogClose asChild>
							<Button type="button" variant="secondary">
								Cancel
							</Button>
						</DialogClose>
						<Button type="submit" disabled={isLoading}>
							<ButtonLoader isLoading={isLoading} />
							Save
						</Button>
					</div>
				</div>
			</form>
		</Form>
	);
};
